// packages/core/src/learning/schools/money-psychology-housel.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'morgan-housel'
// (valid FigureId) — wire into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const MONEY_PSYCHOLOGY_HOUSEL_EXHIBIT: SchoolExhibitEntry = {
	id: 'money-psychology-housel',
	tagline:
		'Money psychology believes your behavior matters more than your spreadsheet.',
	worldviewSummary:
		'Money psychology is paying attention to the human side of money: fear, ego, timing, luck, risk, envy, and the stories people tell themselves. This lens protects you from thinking the smartest plan is always the plan you will actually follow. It accepts that two people can look at the same numbers and make different choices because they have different histories, pressure, and tolerance for uncertainty. The blind spot is that psychology can become too vague if it never gets tested against real numbers.',
	coreBeliefs: [
		'Money psychology believes behavior often beats raw intelligence.',
		'Money psychology believes your lived experience shapes what feels safe, risky, normal, or extreme.',
		'Money psychology believes luck and risk are always in the room, even when people pretend outcomes were fully earned.',
		'Money psychology believes enough is a real skill, not a lack of ambition.',
		'Money psychology believes time, patience, and survival matter more than looking clever.',
		'Money psychology believes a plan that collapses under stress was never really your plan.',
	],
	warnsAgainst: [
		'Confusing a good spreadsheet with a behavior you can repeat under pressure.',
		'Judging other people’s money choices without knowing their history, fear, family pressure, or starting point.',
		'Chasing status until your life gets more expensive but not more free.',
		'Overlearning clever tactics while underlearning patience, humility, and emotional control.',
	],
	helps: 'This lens helps people who know the math but still make emotional money decisions, or people who feel behind and need a calmer way to understand progress. It may not fit if you want a purely technical strategy with clear rules for every decision.',
	figures: [],
	canon: [
		{
			title: 'The Psychology of Money',
			author: 'Morgan Housel',
			oneLineIdea:
				'Money decisions are shaped by behavior, luck, risk, time, and personal history, not just formulas.',
		},
		{
			title: 'Same as Ever',
			author: 'Morgan Housel',
			oneLineIdea:
				'Human behavior changes slower than headlines, which is why old patterns keep showing up in new markets.',
		},
		{
			title: 'Enough',
			oneLineIdea:
				'A stopping point protects you from turning progress into endless comparison.',
		},
		{
			title: 'Luck and risk',
			oneLineIdea:
				'Outcomes are never pure skill or pure failure, so humility belongs in every money story.',
		},
		{
			title: 'Room for error',
			oneLineIdea:
				'A good plan leaves space for bad timing, mistakes, surprise costs, and life being life.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-money-psychology-housel',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare this lens against your real money picture',
			action: { type: 'openDashboard', label: 'Open your Dashboard' },
		},
		{
			label: 'See how runway changes your stress',
			action: { type: 'calculateRunway', label: 'Check your runway' },
		},
		{
			label: 'Test whether your plan survives bad luck',
			action: {
				type: 'openSimulator',
				label: 'Simulate a rougher path',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Define what enough means before pressure gets loud',
			action: {
				type: 'noToolAction',
				label: 'Write your enough point',
				prompt: 'Write one sentence defining what enough would look like for this season of your life.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Money psychology says the spreadsheet is not the whole story.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of ego is a life that gets richer but not calmer.',
			kind: 'hook',
		},
		{
			line: 'What money mistake made perfect sense when you remember what you were afraid of?',
			kind: 'interview-question',
		},
		{
			line: 'When did you realize enough mattered more than looking successful?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps you learn the behavior behind money, then test the tradeoff on your own numbers.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Most apps show numbers. Nisk also teaches why people ignore them.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Ask someone whether their best money move came from math, patience, fear, or luck.',
			kind: 'creator-prompt',
		},
		{
			line: 'Show the same money choice through two people with different pasts and different stress levels.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
